import { nflTeams, collegeTeams, nbaTeams, nhlTeams } from '../data/teams';
import { getFootballSeasonYear, getBasketballHockeySeasonYear } from './season';

const CACHE_TTL_MS = 30 * 60 * 1000;
const cache = new Map();

const getType = (teamName) => {
  if (collegeTeams.some(team => team.name === teamName)) return 'college';
  if (nflTeams.some(team => team.name === teamName)) return 'nfl';
  if (nbaTeams.some(team => team.name === teamName)) return 'nba';
  if (nhlTeams.some(team => team.name === teamName)) return 'nhl';
  return null;
};

const getSeason = (type) => (type === 'nba' || type === 'nhl')
  ? getBasketballHockeySeasonYear()
  : getFootballSeasonYear();

const getSportLabel = (type) => ({
  college: 'College Football',
  nfl: 'NFL',
  nba: 'NBA',
  nhl: 'NHL',
}[type] || 'Sports');

const todayLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCached = (key) => {
  const value = cache.get(key);
  if (!value) return null;
  if (Date.now() - value.savedAt > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return value.data;
};

const putCached = (key, data) => {
  cache.set(key, { savedAt: Date.now(), data });
};

class TicketmasterScheduleAPI {
  static supportsTeam(teamName) {
    return Boolean(getType(teamName));
  }

  static async getTeamSchedule(teamName) {
    const type = getType(teamName);
    if (!type) return null;

    const season = getSeason(type);
    const cacheKey = `${type}:${season}:${teamName}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams({
      team: teamName,
      type,
      season: String(season),
    });

    const response = await fetch(`/api/ticketmaster-schedule?${params.toString()}`);
    let data = {};
    try {
      data = await response.json();
    } catch {
      // Preserve the HTTP status below when the body is not JSON.
    }

    if (!response.ok) {
      const message = data?.message || data?.error || `HTTP ${response.status}`;
      throw new Error(`Ticketmaster schedule failed: ${message}`);
    }

    const schedule = Array.isArray(data?.schedule) ? data.schedule : [];
    if (schedule.length === 0) {
      throw new Error(`Ticketmaster returned no ${season} games for ${teamName}`);
    }

    const result = {
      schedule,
      sport: getSportLabel(type),
      provider: 'Ticketmaster',
      season: data?.season || season,
      attractionId: data?.attractionId || null,
    };

    putCached(cacheKey, result);
    return result;
  }

  static async getUpcomingGames(teamName, limit = 6) {
    const full = await this.getTeamSchedule(teamName);
    if (!full) return null;

    const today = todayLocal();
    const upcoming = full.schedule.filter(game => game.date >= today && !game.isCompleted);

    return {
      ...full,
      schedule: upcoming.slice(0, limit),
      fullSchedule: upcoming,
    };
  }

  static clearCache() {
    cache.clear();
  }

  static getCacheSize() {
    return cache.size;
  }
}

export default TicketmasterScheduleAPI;
